/**
 * UI color palette from design reference
 * Orange/gold, teal, purple, dark purple, reddish-pink, amber-orange, yellow
 */
import type { CSSProperties } from 'react';

export const theme = {
  // Primary palette
  orangeGold: '#f59e0b',
  teal: '#14b8a6',
  purple: '#8b5cf6',
  purpleDark: '#6d28d9',
  pink: '#ec4899',
  amber: '#f97316',
  yellow: '#eab308',

  // Semantic
  primary: '#8b5cf6',      // purple - main actions
  secondary: '#14b8a6',    // teal - secondary actions
  accent: '#f59e0b',       // orange-gold - highlights
  success: '#1a8f3f',     // green
  warning: '#f59e0b',     // orange-gold
  danger: '#ec4899',      // pink
  info: '#8b5cf6',        // purple

  // Neutrals
  bg: '#faf5ff',          // very light purple tint
  bgBase: '#faf5ff',      // solid fallback when bg is gradient
  bgCard: '#ffffff',
  border: '#e9d5ff',      // light purple
  /** Primary foreground (body, headings); distinct from `text` typography scale below */
  textPrimary: '#1e1b4b',
  textMuted: '#6b7280',

  /**
   * Typography tokens. Use `textStyle(token)` helpers (below) instead of inline
   * `fontSize` / `lineHeight` pairs. Sizes are slightly generous on purpose — the product
   * is consumed across desktop + mobile + (soon) non-Latin scripts, and the old inline mix of
   * 0.5625/0.625/0.6875/0.75/0.8125/0.875rem was inconsistent enough that the same "small
   * label" looked 2–3px different between MarketCard and MarketDetail. Five tokens is the
   * cap; resist adding more.
   */
  text: {
    xs:   { size: '0.6875rem', lineHeight: 1.35 }, // all-caps micro labels, timestamps
    sm:   { size: '0.8125rem', lineHeight: 1.45 }, // body small, button labels, chips
    base: { size: '0.875rem',  lineHeight: 1.5  }, // primary body / paragraph
    lg:   { size: '1rem',      lineHeight: 1.5  }, // emphasized body / subheading
    xl:   { size: '1.25rem',   lineHeight: 1.3  }, // headings
  } as const,

  // Legacy heading tokens (kept for back-compat; new code should use `textStyle('xl')`).
  headingSize: '1.25rem',
  headingWeight: 800,

  // Status badges (optional overrides per design)
  statusBgLive: '#dcfce7',
  statusBgClosed: '#fef3c7',
  statusBgExpired: '#e5e7eb',
  statusColorLive: '#166534',
  statusColorClosed: '#92400e',
  statusColorExpired: '#6b7280',
  /** Chip borders paired with status backgrounds (withdraw window, etc.) */
  statusBorderLive: '#bbf7d0',
  statusBorderClosed: '#fde68a',

  /** Danger / success inline alerts (vote errors, confirmations) */
  alertDangerBg: '#fef2f2',
  alertDangerBorder: '#fecaca',
  alertDangerText: '#dc2626',
  alertSuccessBg: '#f0fdf4',
  alertSuccessBorder: '#bbf7d0',
  alertSuccessText: '#16a34a',

  /** Highlighted callout (e.g. “Load more” accent) */
  calloutAccentBg: '#f5f3ff',
  calloutAccentBorder: '#8b5cf6',
  calloutAccentText: '#7c3aed',

  /** Neutral chrome (pagination, inputs) — not the purple `border` token */
  borderNeutral: '#d1d5db',

  /** Text on solid fills (badges, colored buttons, scrims) */
  textOnSolid: '#ffffff',

  /** Voting overlays & table row highlights */
  overlayScrim: 'rgba(107,114,128,0.82)',
  overlayScrimStrong: 'rgba(75,85,99,0.85)',
  overlayVoteLock: '#4b5563',
  voteHighlightYay: 'rgba(34,197,94,0.10)',
  voteHighlightNay: 'rgba(236,72,153,0.10)',
  /** %-figures beside YAY/NAY (live + closed); hue-distinct from `success`/`danger` where needed */
  voteFigureYay: '#15803d',
  voteFigureNay: '#be185d',

  btnDisabledBg: '#9ca3af',
  /** Distinct link-style actions (e.g. creator fee withdraw) */
  accentInteractiveBlue: '#3b82f6',

  shadowSegmented: '0 3px 9px rgba(0,0,0,0.24)',
  shadowDropdown: '0 12px 36px rgba(0,0,0,0.45)',
  shadowPressable: '0 3px 6px rgba(0,0,0,0.15)',
  shadowPopover: '0 12px 42px rgba(0,0,0,0.36)',
  shadowFloating: '0 6px 24px rgba(0,0,0,0.6)',

  // Input fields (optional overrides for dark mode)
  inputBg: undefined as string | undefined,
  inputColor: undefined as string | undefined,
  inputBorder: undefined as string | undefined,

  // Button colors (optional overrides for dark mode - darker bg, light text)
  btnPrimaryBg: undefined as string | undefined,
  btnPrimaryColor: undefined as string | undefined,
  btnSecondaryBg: undefined as string | undefined,
  btnSecondaryColor: undefined as string | undefined,
  btnSuccessBg: undefined as string | undefined,
  btnSuccessColor: undefined as string | undefined,
  btnDangerBg: undefined as string | undefined,
  btnDangerColor: undefined as string | undefined,

  // Card action buttons (Details, BaseScan, Post) and footer
  linkBtnBg: '#e2e8f0',
  linkBtnColor: '#64748b',
  footerBg: undefined as string | undefined,
  navBg: undefined as string | undefined,
  navTextColor: undefined as string | undefined,
  footerTextColor: undefined as string | undefined,
  actionBtnBg: undefined as string | undefined,
  actionBtnSecondaryBg: undefined as string | undefined,

  // Layout & structure (optional overrides per design)
  layout: 'default' as 'default' | 'terminal' | 'immersive',
  /**
   * Selects which homepage market-card layout the feed renders.
   * `classic` keeps the production MarketCard; `orbit` is the market-first
   * redesign surfaced as design mode 8.
   */
  homeCardVariant: 'classic' as 'classic' | 'orbit',
  fontFamily: undefined as string | undefined,
  cardRadius: '0.5rem',
  cardStyle: 'solid' as 'solid' | 'glass' | 'outline',
  cardPadding: undefined as string | undefined,
  maxContentWidth: undefined as string | undefined,
  navStyle: 'bar' as 'bar' | 'compact' | 'floating',
  headingPrefix: undefined as string | undefined,
  cardAccentColor: undefined as string | undefined,
  btnShape: undefined as 'pill' | 'rounded' | undefined,
  btnRadius: undefined as string | undefined,

  /** `chamfer` = 45° corner cuts via clip-path; `round` = classic border-radius */
  cornerStyle: 'chamfer' as 'round' | 'chamfer',
  /** Default chamfer inset for cards / large surfaces */
  chamferCut: '7px',
  /** Buttons and dense controls */
  chamferCutButton: '5px',
  /** Chips, micro badges, stats pills */
  chamferCutMicro: '3px',
};

export type DesignMode = 1 | 2 | 4 | 7 | 8 | 9 | 10;

/** All palettes — admin-only legacy themes plus Orbit, Eclipse, and Groky. */
export const DESIGN_MODES: DesignMode[] = [1, 2, 4, 7, 8, 9, 10];

/** Public palettes — Orbit (light), Groky (warm light), Eclipse (dark Orbit). */
export const PUBLIC_DESIGN_MODES: DesignMode[] = [8, 10, 9];

export const DEFAULT_DESIGN_MODE: DesignMode = 8;

export function isDarkDesignMode(mode: DesignMode): boolean {
  return mode === 2 || mode === 9;
}

export function clampDesignMode(n: number): DesignMode {
  if (n === 1 || n === 2 || n === 4 || n === 7 || n === 8 || n === 9 || n === 10) return n;
  if (n === 3) return 1;
  if (n === 5) return 2;
  if (n === 6) return 4;
  return DEFAULT_DESIGN_MODE;
}

/**
 * Legacy `abc-design-mode` used IDs 1–9 (base, moss, etc.). Maps into the current palette ids (1, 2, 4, 7).
 */
export function migrateLegacyDesignMode(old: number): DesignMode {
  const map: Partial<Record<number, DesignMode>> = {
    1: 1,
    2: 2,
    3: 1,
    4: 1,
    5: 1,
    6: 4,
    7: 2,
    8: 4,
    9: 7,
  };
  const m = map[old];
  if (m !== undefined) return m;
  return clampDesignMode(old);
}

/** Prefer v2 storage; fall back to legacy key once */
export function parseStoredDesignMode(storedV2: string | null, storedV1: string | null): DesignMode {
  if (storedV2 != null && storedV2 !== '') {
    const n = parseInt(storedV2, 10);
    return clampDesignMode(Number.isNaN(n) ? DEFAULT_DESIGN_MODE : n);
  }
  if (storedV1 != null && storedV1 !== '') {
    const n = parseInt(storedV1, 10);
    return migrateLegacyDesignMode(Number.isNaN(n) ? DEFAULT_DESIGN_MODE : n);
  }
  return DEFAULT_DESIGN_MODE;
}

export const DESIGN_MODE_STORAGE_KEY_V2 = 'abc-design-mode-v2';
export const DESIGN_MODE_STORAGE_KEY_V1 = 'abc-design-mode';

/**
 * Chamfer + Roboto neutral chrome — merged under navy / wine / wild-light palettes.
 */
const BASE_THEME_PRESET: Partial<typeof theme> = {
  orangeGold: '#9CA3AF',
  teal: '#6B7280',
  purple: '#525252',
  purpleDark: '#404040',
  pink: '#737373',
  amber: '#A3A3A3',
  yellow: '#D4D4D4',
  primary: '#404040',
  secondary: '#737373',
  accent: '#D4D4D4',
  success: '#525252',
  warning: '#737373',
  danger: '#292929',
  info: '#525252',
  bg: '#F4F4F5',
  bgBase: '#F4F4F5',
  bgCard: '#FAFAFA',
  border: '#E4E4E7',
  textPrimary: '#18181B',
  textMuted: '#71717A',
  statusBgLive: '#F4F4F5',
  statusBgClosed: '#ECECEC',
  statusBgExpired: '#F0F0F0',
  statusColorLive: '#3F3F46',
  statusColorClosed: '#525252',
  statusColorExpired: '#A1A1AA',
  statusBorderLive: '#D4D4D8',
  statusBorderClosed: '#D4D4D8',
  alertDangerBg: '#F4F4F5',
  alertDangerBorder: '#D4D4D8',
  alertDangerText: '#3F3F46',
  alertSuccessBg: '#FAFAFA',
  alertSuccessBorder: '#E4E4E7',
  alertSuccessText: '#525252',
  borderNeutral: '#D4D4D8',
  calloutAccentBg: '#F4F4F5',
  calloutAccentBorder: '#A1A1AA',
  calloutAccentText: '#3F3F46',
  textOnSolid: '#FAFAFA',
  overlayScrim: 'rgba(24, 24, 27, 0.42)',
  overlayScrimStrong: 'rgba(24, 24, 27, 0.5)',
  overlayVoteLock: '#52525B',
  voteHighlightYay: 'rgba(0, 0, 0, 0.05)',
  voteHighlightNay: 'rgba(0, 0, 0, 0.09)',
  voteFigureYay: '#047857',
  voteFigureNay: '#86198f',
  btnDisabledBg: '#D4D4D8',
  accentInteractiveBlue: '#71717A',
  shadowSegmented: '0 3px 6px rgba(24, 24, 27, 0.15), 0 3px 9px 3px rgba(24, 24, 27, 0.09)',
  shadowDropdown: '0 12px 24px 9px rgba(24, 24, 27, 0.15), 0 3px 9px rgba(24, 24, 27, 0.18)',
  shadowPressable: '0 3px 6px rgba(24, 24, 27, 0.12)',
  shadowPopover: '0 18px 36px 12px rgba(24, 24, 27, 0.15), 0 6px 18px 6px rgba(24, 24, 27, 0.12)',
  shadowFloating: '0 12px 24px 9px rgba(24, 24, 27, 0.15), 0 3px 9px rgba(24, 24, 27, 0.18)',
  linkBtnBg: '#ECECEC',
  linkBtnColor: '#525252',
  footerBg: '#EBEBEC',
  footerTextColor: '#71717A',
  navTextColor: '#18181B',
  cardRadius: '0.75rem',
  cardPadding: '1rem 1.25rem',
  fontFamily: "'Roboto', system-ui, sans-serif",
};

/** Visual preview for theme toggle - diagonal split of two palette colors */
export const designPreview: Record<DesignMode, { color1: string; color2: string; borderRadius: string }> = {
  1: { color1: '#8b5cf6', color2: '#e9d5ff', borderRadius: '0.375rem' },
  2: { color1: '#0d9488', color2: '#161b22', borderRadius: '0.375rem' },
  4: { color1: '#9f1239', color2: '#fff1f2', borderRadius: '0.75rem' },
  7: { color1: '#7c3aed', color2: '#fce7f3', borderRadius: '0.75rem' },
  8: { color1: '#4f46e5', color2: '#e0e7ff', borderRadius: '1rem' },
  9: { color1: '#818cf8', color2: '#0f172a', borderRadius: '1rem' },
  10: { color1: '#ff7a17', color2: '#f9f8f6', borderRadius: '1rem' },
};

/** Accessibility copy for the theme segment control */
export const designModeLabels: Record<DesignMode, { ariaLabel: string; title: string }> = {
  1: { ariaLabel: 'Original light theme', title: 'Original lavender palette' },
  2: { ariaLabel: 'Dark theme', title: 'Dark teal palette' },
  4: { ariaLabel: 'Wine theme', title: 'Wine & rose paper' },
  7: { ariaLabel: 'Wild Hyperpop theme', title: 'Wild candy clash' },
  8: { ariaLabel: 'Orbit theme', title: 'Orbit' },
  9: { ariaLabel: 'Eclipse theme', title: 'Eclipse' },
  10: { ariaLabel: 'Groky theme', title: 'Groky' },
};

const CLASSIC_CRIMSON_PATCH: Partial<typeof theme> = {
  orangeGold: '#9f1239',
  teal: '#a16207',
  purple: '#881337',
  purpleDark: '#4c0519',
  pink: '#be123c',
  amber: '#c2410c',
  yellow: '#ca8a04',
  primary: '#9f1239',
  secondary: '#881337',
  accent: '#e11d48',
  success: '#15803d',
  warning: '#c2410c',
  danger: '#9f1239',
  info: '#be1239',
  bg: '#fff1f2',
  bgBase: '#fff1f2',
  bgCard: '#ffffff',
  border: '#fecdd3',
  textPrimary: '#500724',
  textMuted: '#9f1239',
  statusBgLive: '#fce7f3',
  statusBgClosed: '#fff7ed',
  statusBgExpired: '#f5f5f4',
  statusColorLive: '#9f1239',
  statusColorClosed: '#c2410c',
  statusColorExpired: '#78716c',
  statusBorderLive: '#fbcfe8',
  statusBorderClosed: '#fed7aa',
  alertDangerBg: '#fff1f2',
  alertDangerBorder: '#fecdd3',
  alertDangerText: '#9f1239',
  alertSuccessBg: '#f0fdf4',
  alertSuccessBorder: '#bbf7d0',
  alertSuccessText: '#15803d',
  borderNeutral: '#fecdd3',
  calloutAccentBg: '#ffe4e6',
  calloutAccentBorder: '#f43f5e',
  calloutAccentText: '#be123c',
  accentInteractiveBlue: '#e11d48',
  voteHighlightYay: 'rgba(21,128,61,0.10)',
  voteHighlightNay: 'rgba(190,18,60,0.12)',
  voteFigureYay: '#047857',
  voteFigureNay: '#7c3aed',
  linkBtnBg: '#ffe4e6',
  linkBtnColor: '#881337',
  footerBg: '#ffe4e6',
  footerTextColor: '#881337',
  navTextColor: '#500724',
};

const WILD_HYPERPOP_PATCH: Partial<typeof theme> = {
  orangeGold: '#ea580c',
  teal: '#0891b2',
  purple: '#7c3aed',
  purpleDark: '#5b21b6',
  pink: '#db2777',
  amber: '#ea580c',
  yellow: '#fde047',
  primary: '#7c3aed',
  secondary: '#2563eb',
  accent: '#f472b6',
  success: '#059669',
  warning: '#ea580c',
  danger: '#db2777',
  info: '#2563eb',
  bg: '#ede9fe',
  bgBase: '#ede9fe',
  bgCard: '#ffffff',
  border: '#c084fc',
  textPrimary: '#312e81',
  textMuted: '#6d28d9',
  statusBgLive: '#d1fae5',
  statusBgClosed: '#ffedd5',
  statusBgExpired: '#f5f3ff',
  statusColorLive: '#047857',
  statusColorClosed: '#c2410c',
  statusColorExpired: '#7c3aed',
  statusBorderLive: '#6ee7b7',
  statusBorderClosed: '#fdba74',
  alertDangerBg: '#fce7f3',
  alertDangerBorder: '#f9a8d4',
  alertDangerText: '#be185d',
  alertSuccessBg: '#d1fae5',
  alertSuccessBorder: '#6ee7b7',
  alertSuccessText: '#047857',
  borderNeutral: '#ddd6fe',
  calloutAccentBg: '#fae8ff',
  calloutAccentBorder: '#a855f7',
  calloutAccentText: '#6d28d9',
  accentInteractiveBlue: '#2563eb',
  voteHighlightYay: 'rgba(5,150,105,0.14)',
  voteHighlightNay: 'rgba(219,39,119,0.14)',
  voteFigureYay: '#047857',
  voteFigureNay: '#db2777',
  shadowSegmented: '0 2px 10px rgba(91,33,182,0.08), 0 1px 3px rgba(49,46,129,0.06)',
  shadowDropdown: '0 18px 44px rgba(91,33,182,0.14)',
  shadowPressable: '0 2px 8px rgba(91,33,182,0.1)',
  shadowPopover: '0 20px 48px rgba(91,33,182,0.12)',
  shadowFloating: '0 12px 32px rgba(91,33,182,0.11)',
  linkBtnBg: '#f5f3ff',
  linkBtnColor: '#6d28d9',
  footerBg: '#ddd6fe',
  footerTextColor: '#5b21b6',
  navTextColor: '#312e81',
};

/**
 * Orbit (mode 8) — airy light "fintech" palette with rounded corners and Inter.
 * Drives the circular-ring homepage card variant.
 */
const ORBIT_PATCH: Partial<typeof theme> = {
  homeCardVariant: 'orbit',
  layout: 'default',
  cornerStyle: 'round',
  cardRadius: '1rem',
  fontFamily: "'Inter', system-ui, sans-serif",
  orangeGold: '#6366f1',
  teal: '#0ea5e9',
  purple: '#4f46e5',
  purpleDark: '#4338ca',
  pink: '#ec4899',
  amber: '#f59e0b',
  yellow: '#eab308',
  primary: '#4f46e5',
  secondary: '#0ea5e9',
  accent: '#6366f1',
  success: '#16a34a',
  warning: '#f59e0b',
  danger: '#dc2626',
  info: '#2563eb',
  bg: '#f1f5f9',
  bgBase: '#f1f5f9',
  bgCard: '#ffffff',
  border: '#e2e8f0',
  textPrimary: '#0f172a',
  textMuted: '#64748b',
  statusBgLive: '#dcfce7',
  statusBgClosed: '#fef3c7',
  statusBgExpired: '#e2e8f0',
  statusColorLive: '#166534',
  statusColorClosed: '#92400e',
  statusColorExpired: '#475569',
  statusBorderLive: '#bbf7d0',
  statusBorderClosed: '#fde68a',
  borderNeutral: '#cbd5e1',
  calloutAccentBg: '#eef2ff',
  calloutAccentBorder: '#4f46e5',
  calloutAccentText: '#4338ca',
  voteFigureYay: '#15803d',
  voteFigureNay: '#be185d',
  linkBtnBg: '#eef2ff',
  linkBtnColor: '#4338ca',
  footerBg: '#e2e8f0',
  footerTextColor: '#475569',
  navTextColor: '#0f172a',
};

/**
 * Eclipse (mode 9) — dark Orbit: indigo night sky with the same market-first card layout.
 */
const ECLIPSE_PATCH: Partial<typeof theme> = {
  homeCardVariant: 'orbit',
  layout: 'default',
  cornerStyle: 'round',
  cardRadius: '1rem',
  fontFamily: "'Inter', system-ui, sans-serif",
  orangeGold: '#818cf8',
  teal: '#38bdf8',
  purple: '#6366f1',
  purpleDark: '#4f46e5',
  pink: '#f472b6',
  amber: '#fbbf24',
  yellow: '#fde047',
  primary: '#6366f1',
  secondary: '#38bdf8',
  accent: '#818cf8',
  success: '#4ade80',
  warning: '#fbbf24',
  danger: '#f87171',
  info: '#60a5fa',
  bg: '#0f172a',
  bgBase: '#0f172a',
  bgCard: '#1e293b',
  border: '#334155',
  textPrimary: '#f1f5f9',
  textMuted: '#94a3b8',
  statusBgLive: '#1e3a2f',
  statusBgClosed: '#3b2f1a',
  statusBgExpired: '#1e293b',
  statusColorLive: '#4ade80',
  statusColorClosed: '#fbbf24',
  statusColorExpired: '#94a3b8',
  statusBorderLive: '#166534',
  statusBorderClosed: '#92400e',
  alertDangerBg: '#3b1219',
  alertDangerBorder: '#9f1239',
  alertDangerText: '#fecaca',
  alertSuccessBg: '#052e16',
  alertSuccessBorder: '#166534',
  alertSuccessText: '#86efac',
  borderNeutral: '#334155',
  calloutAccentBg: '#1e1b4b',
  calloutAccentBorder: '#6366f1',
  calloutAccentText: '#a5b4fc',
  textOnSolid: '#f8fafc',
  overlayScrim: 'rgba(15,23,42,0.88)',
  overlayScrimStrong: 'rgba(15,23,42,0.92)',
  overlayVoteLock: '#1e293b',
  voteHighlightYay: 'rgba(74,222,128,0.12)',
  voteHighlightNay: 'rgba(244,114,182,0.12)',
  voteFigureYay: '#4ade80',
  voteFigureNay: '#f472b6',
  btnDisabledBg: '#475569',
  accentInteractiveBlue: '#38bdf8',
  shadowSegmented: '0 3px 9px rgba(0,0,0,0.6)',
  shadowDropdown: '0 18px 48px rgba(0,0,0,0.7)',
  shadowPressable: '0 3px 6px rgba(0,0,0,0.5)',
  shadowPopover: '0 18px 48px rgba(0,0,0,0.65)',
  shadowFloating: '0 12px 36px rgba(0,0,0,0.6)',
  linkBtnBg: '#1e1b4b',
  linkBtnColor: '#a5b4fc',
  footerBg: '#0c1222',
  footerTextColor: '#94a3b8',
  navTextColor: '#f1f5f9',
  inputBg: '#0f172a',
  inputColor: '#f1f5f9',
  inputBorder: '#334155',
  btnPrimaryBg: '#312e81',
  btnPrimaryColor: '#f8fafc',
  btnSecondaryBg: '#1e293b',
  btnSecondaryColor: '#f8fafc',
  actionBtnBg: '#312e81',
  actionBtnSecondaryBg: '#1e293b',
  btnSuccessBg: '#14532d',
  btnSuccessColor: '#f8fafc',
  btnDangerBg: '#7f1d1d',
  btnDangerColor: '#fef2f2',
};

/**
 * Groky (mode 10) — warm Grok/xAI light: cream paper, jet-ink chrome, sunset accent.
 * Same market-first Orbit card layout as modes 8/9.
 */
const GROKY_PATCH: Partial<typeof theme> = {
  homeCardVariant: 'orbit',
  layout: 'default',
  cornerStyle: 'round',
  cardRadius: '1rem',
  btnShape: 'pill',
  fontFamily: "'Inter', system-ui, sans-serif",
  orangeGold: '#ff7a17',
  teal: '#545454',
  purple: '#3b3b3b',
  purpleDark: '#151515',
  pink: '#ff5f57',
  amber: '#ffbd2e',
  yellow: '#ffbd2e',
  primary: '#0a0a0a',
  secondary: '#545454',
  accent: '#ff7a17',
  success: '#16a34a',
  warning: '#ffbd2e',
  danger: '#ff5f57',
  info: '#3b3b3b',
  bg: '#f9f8f6',
  bgBase: '#f9f8f6',
  bgCard: '#ffffff',
  border: '#d5d9e2',
  textPrimary: '#0a0a0a',
  textMuted: '#858585',
  statusBgLive: '#ecfdf3',
  statusBgClosed: '#fff6e0',
  statusBgExpired: '#f2ede5',
  statusColorLive: '#166534',
  statusColorClosed: '#92400e',
  statusColorExpired: '#545454',
  statusBorderLive: '#bbf7d0',
  statusBorderClosed: '#fde68a',
  alertDangerBg: '#fff1f0',
  alertDangerBorder: '#ffc9c5',
  alertDangerText: '#b42318',
  alertSuccessBg: '#ecfdf3',
  alertSuccessBorder: '#bbf7d0',
  alertSuccessText: '#166534',
  borderNeutral: '#d5d9e2',
  calloutAccentBg: '#fff4eb',
  calloutAccentBorder: '#ff7a17',
  calloutAccentText: '#c2410c',
  textOnSolid: '#ffffff',
  overlayScrim: 'rgba(10, 10, 10, 0.42)',
  overlayScrimStrong: 'rgba(10, 10, 10, 0.52)',
  overlayVoteLock: '#3b3b3b',
  voteHighlightYay: 'rgba(22, 163, 74, 0.10)',
  voteHighlightNay: 'rgba(255, 95, 87, 0.12)',
  voteFigureYay: '#15803d',
  voteFigureNay: '#be185d',
  btnDisabledBg: '#d5d9e2',
  accentInteractiveBlue: '#ff7a17',
  shadowSegmented: '0 2px 8px rgba(10, 10, 10, 0.08)',
  shadowDropdown: '0 16px 40px rgba(10, 10, 10, 0.12)',
  shadowPressable: '0 1px 3px rgba(10, 10, 10, 0.08)',
  shadowPopover: '0 18px 44px rgba(10, 10, 10, 0.12)',
  shadowFloating: '0 10px 28px rgba(10, 10, 10, 0.10)',
  linkBtnBg: '#f2ede5',
  linkBtnColor: '#0a0a0a',
  footerBg: '#f2ede5',
  footerTextColor: '#545454',
  navTextColor: '#0a0a0a',
  btnPrimaryBg: '#0a0a0a',
  btnPrimaryColor: '#ffffff',
  btnSecondaryBg: '#f2ede5',
  btnSecondaryColor: '#0a0a0a',
};

/** Per-design theme overrides */
const designThemes: Record<DesignMode, Partial<typeof theme>> = {
  1: {
    primary: '#8b5cf6',
    info: '#8b5cf6',
    bg: '#faf5ff',
    border: '#e9d5ff',
    voteFigureYay: '#15803d',
    voteFigureNay: '#be185d',
  },
  2: {
    primary: '#0d9488',
    secondary: '#2dd4bf',
    accent: '#94a3b8',
    info: '#0d9488',
    warning: '#94a3b8',
    teal: '#0d9488',
    amber: '#94a3b8',
    yellow: '#94a3b8',
    purple: '#94a3b8',
    pink: '#cbd5e1',
    orangeGold: '#94a3b8',
    bg: '#0c0f14',
    bgBase: '#0c0f14',
    bgCard: '#161b22',
    border: '#30363d',
    textPrimary: '#f8fafc',
    textMuted: '#cbd5e1',
    statusBgLive: '#1e293b',
    statusBgClosed: '#1e293b',
    statusBgExpired: '#1e293b',
    statusColorLive: '#2dd4bf',
    statusColorClosed: '#cbd5e1',
    statusColorExpired: '#cbd5e1',
    statusBorderLive: '#334155',
    statusBorderClosed: '#334155',
    alertDangerBg: '#2a1212',
    alertDangerBorder: '#7f1d1d',
    alertDangerText: '#fecaca',
    alertSuccessBg: '#042f2e',
    alertSuccessBorder: '#0f766e',
    alertSuccessText: '#5eead4',
    borderNeutral: '#30363d',
    textOnSolid: '#f8fafc',
    overlayScrim: 'rgba(15,23,42,0.88)',
    overlayScrimStrong: 'rgba(15,23,42,0.92)',
    overlayVoteLock: '#1e293b',
    voteHighlightYay: 'rgba(45,212,191,0.12)',
    voteHighlightNay: 'rgba(244,63,94,0.12)',
    voteFigureYay: '#5eead4',
    voteFigureNay: '#fb7185',
    btnDisabledBg: '#475569',
    accentInteractiveBlue: '#38bdf8',
    calloutAccentBg: '#0f172a',
    calloutAccentBorder: '#0d9488',
    calloutAccentText: '#5eead4',
    shadowSegmented: '0 3px 9px rgba(0,0,0,1)',
    shadowDropdown: '0 24px 72px rgba(0,0,0,1)',
    shadowPressable: '0 3px 6px rgba(0,0,0,1)',
    shadowPopover: '0 24px 72px rgba(0,0,0,1)',
    shadowFloating: '0 12px 48px rgba(0,0,0,1)',
    linkBtnBg: '#0f172a',
    linkBtnColor: '#f8fafc',
    navTextColor: '#f8fafc',
    footerTextColor: '#cbd5e1',
    footerBg: '#0a0d12',
    inputBg: '#000000',
    inputColor: '#fafafa',
    inputBorder: '#0f172a',
    btnPrimaryBg: '#000a08',
    btnPrimaryColor: '#f8fafc',
    btnSecondaryBg: '#020408',
    btnSecondaryColor: '#f8fafc',
    actionBtnBg: '#000a08',
    actionBtnSecondaryBg: '#020408',
    success: '#0f766e',
    danger: '#e11d48',
    btnSuccessBg: '#000a08',
    btnSuccessColor: '#f8fafc',
    btnDangerBg: '#1a0510',
    btnDangerColor: '#fef2f2',
  },
  4: { ...BASE_THEME_PRESET, ...CLASSIC_CRIMSON_PATCH },
  7: { ...BASE_THEME_PRESET, ...WILD_HYPERPOP_PATCH },
  8: ORBIT_PATCH,
  9: ECLIPSE_PATCH,
  10: GROKY_PATCH,
};

export function getThemeForDesignMode(mode: DesignMode) {
  return {
    ...theme,
    ...designThemes[mode],
  };
}

/**
 * Typography helper: returns a `{ fontSize, lineHeight }` pair for the given token.
 * Usage: `style={{ ...textStyle('sm'), color: theme.textMuted }}`.
 *
 * The token list is intentionally small (xs / sm / base / lg / xl). If you feel the urge to
 * add `mdSmall` or `smPlus`, step back and use whichever existing token is closest — the whole
 * point is visual consistency across surfaces.
 */
export type TextToken = keyof typeof theme.text;
export function textStyle(token: TextToken): { fontSize: string; lineHeight: number } {
  const t = theme.text[token];
  return { fontSize: t.size, lineHeight: t.lineHeight };
}

/** Octagonal clip (equal 45° cuts) — `cutPx` is distance along each edge from the corner */
export function chamferClipPath(cutPx: string): string {
  const c = cutPx.trim();
  return `polygon(${c} 0%, calc(100% - ${c}) 0%, 100% ${c}, 100% calc(100% - ${c}), calc(100% - ${c}) 100%, ${c} 100%, 0% calc(100% - ${c}), 0% ${c})`;
}

export function themeCornerIsChamfer(t: typeof theme): boolean {
  return t.cornerStyle === 'chamfer';
}

/** Inline clip for chips / tiny badges (stats row, etc.) */
export function getMicroChamferStyle(t: typeof theme): { borderRadius: number; clipPath: string; WebkitClipPath: string } | Record<string, never> {
  if (!themeCornerIsChamfer(t)) return {};
  const cp = chamferClipPath(t.chamferCutMicro ?? '3px');
  return { borderRadius: 0, clipPath: cp, WebkitClipPath: cp };
}

/**
 * Rectangular buttons / controls — prefer spreading this over raw `borderRadius`
 * when corners should follow the active corner style.
 */
export function getButtonSurfaceStyle(t: typeof theme): CSSProperties {
  if (themeCornerIsChamfer(t)) {
    const cp = chamferClipPath(t.chamferCutButton ?? '5px');
    return { borderRadius: 0, clipPath: cp, WebkitClipPath: cp };
  }
  return { borderRadius: getButtonRadius(t) };
}

/** Button border radius from theme - pill, rounded, or default (ignored when chamfer) */
export function getButtonRadius(t: typeof theme): string {
  if (themeCornerIsChamfer(t)) return '0';
  return t.btnRadius ?? (t.btnShape === 'pill' ? '9999px' : '0.375rem');
}

function chamferCardFilters(t: typeof theme, elevation: string): string {
  const stroke = `drop-shadow(0 0 0 3px ${t.border})`;
  return `${stroke} ${elevation}`.trim();
}

/** Card base style from theme - handles solid, glass, outline */
export function getCardStyle(t: typeof theme, opts?: { skipAccent?: boolean }): Record<string, string | number | undefined> {
  const padding = (t.cardPadding as string) ?? '0.875rem 1rem';
  const accentBorder =
    t.cardAccentColor && !opts?.skipAccent ? ({ borderLeft: `4px solid ${t.cardAccentColor}` } as const) : {};

  if (themeCornerIsChamfer(t)) {
    const cut = t.chamferCut ?? '7px';
    const cp = chamferClipPath(cut);
    const baseChamfer: Record<string, string | number | undefined> = {
      backgroundColor: t.bgCard,
      borderRadius: 0,
      border: 'none',
      clipPath: cp,
      WebkitClipPath: cp,
      padding,
      ...accentBorder,
    };
    if (t.cardStyle === 'glass') {
      return {
        ...baseChamfer,
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        filter: chamferCardFilters(t, 'drop-shadow(0 18px 72px rgba(0,0,0,1))'),
        boxShadow: 'none',
      };
    }
    if (t.cardStyle === 'outline') {
      return {
        ...baseChamfer,
        filter: chamferCardFilters(t, ''),
        boxShadow: 'none',
      };
    }
    return {
      ...baseChamfer,
      filter: chamferCardFilters(t, 'drop-shadow(0 3px 9px rgba(0, 0, 0, 0.24))'),
      boxShadow: 'none',
    };
  }

  const base: Record<string, string | number | undefined> = {
    backgroundColor: t.bgCard,
    borderRadius: t.cardRadius ?? '0.5rem',
    border: `1px solid ${t.border}`,
    ...accentBorder,
    padding,
  };
  if (t.cardStyle === 'glass') {
    return {
      ...base,
      backgroundColor: t.bgCard,
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      boxShadow: '0 12px 72px rgba(0,0,0,0.6)',
    };
  }
  if (t.cardStyle === 'outline') {
    return { ...base, boxShadow: 'none' };
  }
  return { ...base, boxShadow: '0 3px 9px 0 rgba(0, 0, 0, 0.3)' };
}
